import { isMiniappFolder } from '@papan/types'
import type { AppCodeContext } from './types.js'

/**
 * What the completions may learn about the app the edited file belongs to: its
 * op files, an op's `:params`, its schema and its scripts — read through the
 * drive API as the person editing, so it never shows them anything their drive
 * doesn't. Nothing is fetched until a completion asks, answers are cached for
 * `TTL_MS`, and every failure (no app, no `_db/`, no access, a share view whose
 * API acts as the link) answers empty.
 */

export interface DriveReader {
  getBreadcrumbs(fileId: string): Promise<Array<{ id: string; name: string }>>
  resolvePath(path: string, baseId?: string): Promise<{ id: string } | null>
  listChildren(parentId: string, options?: { limit?: number }): Promise<{ files: Array<{ id: string; name: string; isFolder: boolean }> }>
  getFileContent(fileId: string): Promise<Blob>
}

const TTL_MS = 30_000
/** The `_db/` walk mirrors the op bundle's (features/appdb/server/bundle.ts) at a smaller scale. */
const MAX_DEPTH = 4
const MAX_FILES = 300
const LIST_LIMIT = 500

/** The `:name` caller args an op's SQL declares (strings and comments skipped, `::` casts ignored). */
export function lexOpParams(sql: string): string[] {
  const text = sql
    .replace(/\/\*[\s\S]*?\*\//g, ' ')
    .replace(/--[^\n]*/g, ' ')
    .replace(/'(?:[^']|'')*'/g, "''")
    .replace(/"(?:[^"]|"")*"/g, '""')
  const out: string[] = []
  for (const m of text.matchAll(/(?<![:\w]):([A-Za-z_]\w*)/g)) if (!out.includes(m[1]!)) out.push(m[1]!)
  return out
}

export function createAppCodeContext(fileId: string, api: DriveReader): AppCodeContext {
  const cache = new Map<string, { at: number; value: Promise<unknown> }>()
  function memo<T>(key: string, load: () => Promise<T>, empty: T): Promise<T> {
    const hit = cache.get(key)
    if (hit && Date.now() - hit.at < TTL_MS) return hit.value as Promise<T>
    const value = load().catch(() => empty)
    cache.set(key, { at: Date.now(), value })
    return value
  }

  /** The app folder: the nearest `.pdapp` ancestor, else the folder holding the
   *  `_db/` / `_api/` this file sits in, else the file's own folder (a Mode A page). */
  const appRoot = () => memo('root', async () => {
    const crumbs = await api.getBreadcrumbs(fileId)
    const folders = crumbs[crumbs.length - 1]?.id === fileId ? crumbs.slice(0, -1) : crumbs
    for (let i = folders.length - 1; i >= 0; i--) if (isMiniappFolder(folders[i]!.name)) return folders[i]!.id
    for (let i = folders.length - 1; i >= 1; i--) if (folders[i]!.name === '_db' || folders[i]!.name === '_api') return folders[i - 1]!.id
    return folders[folders.length - 1]?.id ?? null
  }, null as string | null)

  /** Files under `<root>/<dir>/` matching `keep`, as `dir/sub/name` → file id. */
  const walk = (dir: string, keep: (name: string) => boolean) => memo(`walk:${dir}`, async () => {
    const out = new Map<string, string>()
    const root = await appRoot()
    if (!root) return out
    const top = await api.resolvePath(dir, root)
    if (!top) return out
    const visit = async (folderId: string, rel: string, depth: number): Promise<void> => {
      const { files } = await api.listChildren(folderId, { limit: LIST_LIMIT })
      for (const f of files) {
        if (out.size >= MAX_FILES) return
        if (f.isFolder) {
          // Like the bundle walker: `.` / `_` subfolders hold no ops.
          if (depth < MAX_DEPTH && !f.name.startsWith('.') && !f.name.startsWith('_')) await visit(f.id, `${rel}/${f.name}`, depth + 1)
        } else if (keep(f.name)) {
          out.set(`${rel}/${f.name}`, f.id)
        }
      }
    }
    await visit(top.id, dir, 1)
    return out
  }, new Map<string, string>())

  const ops = () => walk('_db', (n) => /\.sql$/i.test(n))
  const readText = async (id: string) => (await api.getFileContent(id)).text()

  return {
    async opPaths() {
      return [...(await ops()).keys()].filter((p) => p !== '_db/schema.sql').sort()
    },
    opParams(opPath) {
      const path = /\.sql$/i.test(opPath) ? opPath : `_db/${opPath}.sql`
      return memo(`params:${path}`, async () => {
        const id = (await ops()).get(path)
        return id ? lexOpParams(await readText(id)) : []
      }, [] as string[])
    },
    schemaSql() {
      return memo('schema', async () => {
        const id = (await ops()).get('_db/schema.sql')
        return id ? readText(id) : ''
      }, '')
    },
    async scriptPaths() {
      return [...(await walk('_api', (n) => /\.(py|sh)$/i.test(n))).keys()].sort()
    },
  }
}
